import React, {useEffect, useMemo, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { TabView, TabPanel } from "primereact/tabview";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import agent from "../../agent";
import { DASHBOARD_COUNTS_LOAD, DASHBOARD_COUNTS_UNLOAD, DASHBOARD_QUEUES_LOAD, DASHBOARD_QUEUES_UNLOAD } from "../../constants/actionTypes";

function Dashboard() {
  const toast = useRef(null);
  const dispatch = useDispatch();
  const dashboardState = useSelector((state) => state.dashboard);
  const counts = dashboardState?.data || {};
  const rabbitQueues = dashboardState?.rabbitQueues || [];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Tabs control: 0 - New/Overdue, 1 - Fails
  const [activeIndex, setActiveIndex] = useState(0);

  // New/Overdue pagination and data
  const [newRows, setNewRows] = useState(20);
  const [newFirst, setNewFirst] = useState(0);
  const [newData, setNewData] = useState([]);
  const [newTotal, setNewTotal] = useState(0);
  const [newLoading, setNewLoading] = useState(false);

  // Fails pagination and data
  const [failRows, setFailRows] = useState(20);
  const [failFirst, setFailFirst] = useState(0);
  const [failData, setFailData] = useState([]);
  const [failTotal, setFailTotal] = useState(0);
  const [failLoading, setFailLoading] = useState(false);
  const [queuesLoading, setQueuesLoading] = useState(false);
  const [queuesAccordionIndex, setQueuesAccordionIndex] = useState(null);

  // Polling controls
  const POLL_OPTIONS = [
    { label: '10 сек', value: 10000 },
    { label: '30 сек', value: 30000 },
    { label: '1 мин', value: 60000 },
  ];
  const [pollInterval, setPollInterval] = useState(60000);
  const [refreshTick, setRefreshTick] = useState(0);
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  const loadRabbitQueues = async () => {
      setQueuesLoading(true);
      try {
          const data = await agent.Dashboard.getRabbitQueue();
          dispatch({ type: DASHBOARD_QUEUES_LOAD, payload: Array.isArray(data) ? data : (data?.queues ?? []) });
      } catch (e) {
          // optional: handle error
      } finally {
          setQueuesLoading(false);
      }
  }

  const loadCounts = async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await agent.Dashboard.getCounts();
      dispatch({ type: DASHBOARD_COUNTS_LOAD, payload: data });
    } catch (e) {
      setError("Не удалось загрузить данные");
    } finally {
      setLoading(false);
    }
  };

  const manualRefresh = () => {
    setAutoRefreshing(true);
    Promise.all([loadCounts(), loadRabbitQueues()])
      .catch(() => {})
      .finally(() => {
        setRefreshTick((t) => t + 1);
        setTimeout(() => setAutoRefreshing(false), 600);
      });
  };

  // Helpers to extract pageable or array response
  const normalizePage = (resp, fallbackSize = 20) => {
    if (!resp) return { content: [], totalElements: 0, size: fallbackSize, number: 0 };
    if (Array.isArray(resp)) return { content: resp, totalElements: resp.length, size: fallbackSize, number: 0 };

    // Support multiple backend shapes:
    // 1) Spring-like: { content, totalElements, size, number }
    // 2) Custom: { items, total, size, page }
    // 3) Fallbacks
    const content = (resp.items ?? resp.content ?? []);
    const totalElements = (resp.total ?? resp.totalElements ?? content.length ?? 0);
    const size = (resp.size ?? fallbackSize);
    const number = (resp.page ?? resp.number ?? 0);
    return { content, totalElements, size, number };
  };

  // Flatten incoming items so both shapes (flat or with nested task) work
  const flattenItem = (item) => {
    if (!item) return item;
    const t = item.task || {};
    const flat = {
      orderNum: item.orderNum ?? t.orderNum ?? null,
      childOrderNum: item.childOrderNum ?? null,
      postalService: item.postalService ?? null,
      postalServiceCode: item.postalServiceCode ?? null,
      id: item.id ?? t.id ?? item.orderNum ?? t.orderNum ?? undefined,
      retryCount: item.retryCount ?? t.retryCount ?? null,
      taskStatusName: item.taskStatusName ?? t.taskStatusName ?? null,
      taskStatus: item.taskStatus ?? t.taskStatus ?? null,
      taskType: item.taskType ?? t.taskType ?? null,
      createdDate: item.createdDate ?? t.createdDate ?? null,
      startTime: item.startTime ?? t.startTime ?? null,
      endTime: item.endTime ?? t.endTime ?? null,
      nextTime: item.nextTime ?? t.nextTime ?? null,
      errorMessage: item.errorMessage ?? t.errorMessage ?? null,
      errorTrace: item.errorTrace ?? t.errorTrace ?? null,
    };
    return flat;
  };

  useEffect(() => {
    Promise.all([loadCounts(), loadRabbitQueues()]).then(r =>
        console.log(r)
    );

    return () => {
      dispatch({ type: DASHBOARD_COUNTS_UNLOAD });
      dispatch({ type: DASHBOARD_QUEUES_UNLOAD });
    };
  }, [dispatch]);

  useEffect(() => {
    if (!pollInterval) return;
    const id = setInterval(() => {
      setAutoRefreshing(true);
      Promise.all([loadCounts(), loadRabbitQueues()])
        .catch(() => {})
        .finally(() => {
          setRefreshTick((t) => t + 1);
          setTimeout(() => setAutoRefreshing(false), 600);
        });
    }, pollInterval);
    return () => clearInterval(id);
  }, [pollInterval]);

  useEffect(() => {
    const fetchNew = async () => {
      setNewLoading(true);
      try {
        const pageIndex = Math.floor(newFirst / newRows);
        const resp = await agent.Dashboard.getNewOverdue(pageIndex, newRows);
        const page = normalizePage(resp, newRows);
        setNewData((page.content || []).map(flattenItem));
        setNewTotal(page.totalElements || 0);
      } catch (e) {
          setError("Возникла ошибка в загрузке списка Новые/Просроченные")
      } finally {
        setNewLoading(false);
      }
    };
    fetchNew();
  }, [newFirst, newRows, refreshTick]);

  // Fetch Fails only when tab is active and pagination changes
  useEffect(() => {
    if (activeIndex !== 1) return;
    const fetchFails = async () => {
      setFailLoading(true);
      try {
        const pageIndex = Math.floor(failFirst / failRows);
        const resp = await agent.Dashboard.getFailedTasks(pageIndex, failRows);
        const page = normalizePage(resp, failRows);
        setFailData((page.content || []).map(flattenItem));
        setFailTotal(page.totalElements || 0);
      } catch (e) {
        // optional: handle error
      } finally {
        setFailLoading(false);
      }
    };
    fetchFails();
  }, [activeIndex, failFirst, failRows, refreshTick]);

  const StatCard = ({ title, value, severity }) => (
    <Card className="p-shadow-2" title={title}>
      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: severity === 'danger' ? '#ef4444' : severity === 'success' ? '#16a34a' : 'inherit' }}>
        {value ?? 0}
      </div>
    </Card>
  );

  const renderGenericColumns = (sample) => {
    if (!sample) return null;
    const keys = Object.keys(sample);
    const showKeys = keys.slice(0, 6);
    return showKeys.map((k) => (
      <Column key={k} field={k} header={k} />
    ));
  };

  const dashIfNull = (v) => (v === null || v === undefined || v === '' ? '—' : v);
  const numberOrDash = (v) => (v === null || v === undefined ? '—' : v);

  const QUEUE_NAME_MAP = {
    'orders-send-queue': 'Отправка заказов',
    'orders-send-queue-2': 'Отправка заказов 2',
    'dpd-pending-queue': 'Проверка адреса (ARCGIS) DPD',
    'call-courier-queue': 'Вызов курьера',
    'download-label-queue': 'Загрузка ярлыка',
    'check-parcel-queue': 'Проверка посылки',
    'cancel-order-queue': 'Отмена заказа',
    'pause-order-queue': 'Пауза заказа',
    'update-receiver-queue': 'Обновление получателя',
    'notification-queue': 'Уведомления',
  };

  const humanizeQueueName = (q) => {
    if (!q) return '—';
    const direct = QUEUE_NAME_MAP[q];
    if (direct) return direct;
    // Fallback: replace dashes with spaces and capitalize first letter
    const pretty = String(q)
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
  };

  const queueBody = (row) => (
    <div>
      <div style={{ fontWeight: 600 }}>{humanizeQueueName(row.queue)}</div>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'monospace' }}>{dashIfNull(row.queue)}</div>
    </div>
  );
  const capacityBody = (row) => <Tag value={numberOrDash(row.capacity)} severity="info" rounded />;
  const sizeBody = (row) => (
    <Tag value={numberOrDash(row.size)} severity={row.size > 0 ? 'warning' : 'success'} rounded />
  );
  const consumersBody = (row) => (
    <Tag value={numberOrDash(row.consumers)} severity={row.consumers > 0 ? 'success' : 'danger'} rounded />
  );
  const avgTimeBody = (row) => (
    row.avgProcessingSeconds !== null && row.avgProcessingSeconds !== undefined
      ? <Tag value={`${Number(row.avgProcessingSeconds).toFixed(2)}s`} severity="secondary" rounded />
      : <span>—</span>
  );
  const taskTypeBody = (row) => (
    row.taskType ? <Tag value={row.taskType} severity="info" /> : <span>—</span>
  );

  const syncOrderCount = async () => {
      try {
          const data = await agent.Dashboard.syncOrderCount();
          console.log(data);
          toast.current?.show({ severity: "success", summary: "Сохранено", detail: `Каунтер на сайте обновлен ${data?.totalCount ?? ''}`.trim(), life: 3000 });
      } catch (e) {
          toast.current?.show({ severity: "error", summary: "Ошибка", detail: e?.message || "Не удалось обновить каунтер", life: 3000 });
      }
  }
  return (
    <div className="p-p-4">
      <Toast ref={toast} />
      <div className="p-d-flex p-ai-center p-jc-between p-mb-3">
        <h2 className="p-m-0">Административная панель</h2>
        <div className="p-d-flex p-ai-center" style={{ gap: 8 }}>
          <Dropdown value={pollInterval} options={POLL_OPTIONS} onChange={(e) => setPollInterval(e.value)} placeholder="Автообновление" />
          {autoRefreshing && <ProgressSpinner style={{ width: '1.5rem', height: '1.5rem' }} strokeWidth="6" />}
          <Button label="Обновить" icon="pi pi-refresh" onClick={manualRefresh} disabled={loading || queuesLoading} />
          <Button label="Обновить каунтер на сайте" icon="pi pi-refresh" onClick={syncOrderCount} disabled={loading || queuesLoading} />
        </div>
      </div>

      {error && (
        <div className="p-p-3 p-mb-3" style={{ border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 6 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-d-flex p-jc-center p-my-6">
          <ProgressSpinner />
        </div>
      ) : (
        <>
          <div className="p-grid p-mb-4">
            <div className="p-col-12 p-md-3">
              <StatCard title="Всего" value={counts?.counts} />
            </div>
            <div className="p-col-12 p-md-3">
              <StatCard title="Новые" value={counts?.new} severity="success" />
            </div>
            <div className="p-col-12 p-md-3">
              <StatCard title="Ошибки" value={counts?.fail} severity="danger" />
            </div>
            <div className="p-col-12 p-md-3">
              <StatCard title="Начато" value={counts?.begin} />
            </div>
          </div>

          <div className="card p-mb-4">
            <Accordion activeIndex={queuesAccordionIndex} onTabChange={(e) => setQueuesAccordionIndex(e.index)}>
              <AccordionTab header={
                <div className="p-d-flex p-ai-center p-jc-between" style={{ width: '100%' }}>
                  <span>Очереди RabbitMQ</span>
                  <Button label="Обновить очереди" icon="pi pi-sync" className="p-button-text" onClick={(e) => { e.stopPropagation(); loadRabbitQueues(); }} disabled={queuesLoading} />
                </div>
              }>
                <div className="p-p-3">
                  <DataTable
                    value={rabbitQueues}
                    stripedRows
                    responsiveLayout="scroll"
                    emptyMessage="Очереди не найдены"
                  >
                    <Column field="queue" header="Очередь" body={queueBody} sortable />
                    <Column field="capacity" header="Вместимость" body={capacityBody} />
                    <Column field="size" header="Размер" body={sizeBody} />
                    <Column field="consumers" header="Потребители" body={consumersBody} />
                    <Column field="avgProcessingSeconds" header="Сред. время" body={avgTimeBody} />
                    <Column field="taskType" header="Тип задачи" body={taskTypeBody} />
                  </DataTable>
                  {queuesLoading && (
                    <div className="p-d-flex p-jc-center p-my-3"><ProgressSpinner /></div>
                  )}
                </div>
              </AccordionTab>
            </Accordion>
          </div>

          <div className="card">
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
              <TabPanel header={`Новые (${counts?.new ?? 0})`}>
                <div className="p-p-3">
                  <h3 className="p-mt-0">Новые/Просроченные</h3>
                  <DataTable
                    value={newData}
                    lazy
                    paginator
                    totalRecords={newTotal}
                    rows={newRows}
                    first={newFirst}
                    onPage={(e) => {
                      setNewRows(e.rows);
                      setNewFirst(e.first);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    dataKey="id"
                    emptyMessage="Данные не найдены"
                    currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  >
                    <Column field="orderNum" header="Заказ" />
                    <Column field="childOrderNum" header="Дочерний заказ" />
                    <Column field="postalService" header="Служба" />
                    <Column field="postalServiceCode" header="Код службы" />

                    <Column field="taskType" header="Тип задачи" />
                    <Column field="taskStatusName" header="Статус" />
                    <Column field="createdDate" header="Создано" />
                    <Column field="startTime" header="Старт" />
                    <Column field="endTime" header="Завершено" />
                    <Column field="nextTime" header="След. время" />
                    <Column field="retryCount" header="Повторы" />
                  </DataTable>
                  {newLoading && (
                    <div className="p-d-flex p-jc-center p-my-4"><ProgressSpinner /></div>
                  )}
                </div>
              </TabPanel>
              <TabPanel header={`Ошибки (${counts?.fail ?? 0})`}>
                <div className="p-p-3">
                  <h3 className="p-mt-0">Ошибки</h3>
                  <DataTable
                    value={failData}
                    lazy
                    paginator
                    totalRecords={failTotal}
                    rows={failRows}
                    first={failFirst}
                    onPage={(e) => {
                      setFailRows(e.rows);
                      setFailFirst(e.first);
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    dataKey="id"
                    emptyMessage="Данные не найдены"
                    currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                  >
                    <Column field="orderNum" header="Заказ" />
                    <Column field="childOrderNum" header="Дочерний заказ" />
                    <Column field="postalService" header="Служба" />
                    <Column field="postalServiceCode" header="Код службы" />

                    <Column field="taskType" header="Тип задачи" />
                    <Column field="taskStatusName" header="Статус" />
                    <Column field="createdDate" header="Создано" />
                    <Column field="startTime" header="Старт" />
                    <Column field="endTime" header="Завершено" />
                    <Column field="nextTime" header="След. время" />
                    <Column field="retryCount" header="Повторы" />
                    <Column field="errorMessage" header="Ошибка" />
                  </DataTable>
                  {failLoading && (
                    <div className="p-d-flex p-jc-center p-my-4"><ProgressSpinner /></div>
                  )}
                </div>
              </TabPanel>
            </TabView>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;